import { useCallback } from 'react';
import { useSweepApi } from '../sweep';
import { ObjectData, NameProperties, OpenAiResponse } from '../../components/parser/ParserTypes';
import { BackendDocumentationMetadataTypesTab } from '../../components/documentation/tabs/types';
import {
  SearchResponse,
  ContentSearchResponse,
} from '../../components/documentation/universal-search/types';
import {
  AiChatDto,
} from '../../components/common/ai-chat/aiChatTypes';
import buildURLQuery from '../../lib/buildURLQuery';
import { AiChatConfigurationContext, AiChatFieldContext } from '@server/ai';
import { groupBy } from 'lodash';

export type NameSearchProperties = NameProperties & {
  metadataId: string;
};
import { ConfigurationConsumers, ConfigurationDependencies } from '../../components/documentation/dependencies/DependenciesTypes';
import { BaseElementProperties, LayoutProperties } from '@server/documentation';

interface AiChatBasePayload {
    sessionId: string;
    message: string;
    isFirstMessage: boolean;
    crmOrgId: string;
  }

export interface AiChatFieldPayload extends AiChatBasePayload {
    context: AiChatFieldContext;
}

export interface AiChatConfigurationPayload extends AiChatBasePayload {
    context: AiChatConfigurationContext;
}

const transformSearchResponse = (response: ContentSearchResponse | SearchResponse) => {
    const _response = response as ContentSearchResponse;
    delete _response?.files; //by product decision files are not supported atm

    const { unclassifiedConfigurations, ...rest } = _response;
    const unclassifiedConfigurationsByType = groupBy(unclassifiedConfigurations, 'type');

    return { ...rest, ...unclassifiedConfigurationsByType };
};


export const useDocumentationApiFacade = () => {
  const sweepApi = useSweepApi();

  return {
    post_createObject: useCallback(
      async (objectName: string, { payload }: { payload: { visible: boolean } }) => {
        const response = await sweepApi.post<ObjectData>(
          `/documentation/objects/${objectName}`,
          payload,
        );
        return response.data;
      },
      [sweepApi],
    ),
    patch_toggleObjectVisibility: useCallback(
      async (objectName: string, visible: boolean) => {
        return await sweepApi.patch(`/documentation/objects/${objectName}`, {
          visible,
        });
      },
      [sweepApi],
    ),
    get_metadataTypes: useCallback(async (): Promise<BackendDocumentationMetadataTypesTab[]> => {
      const response = await sweepApi.get<BackendDocumentationMetadataTypesTab[]>(
        `/documentation/metadataTypes`,
      );
      return response.data;
    }, [sweepApi]),
    get_metadataData: useCallback(
      async (metadataTypeName: string) => {
        const response = await sweepApi.get<BaseElementProperties[]>(
          `/documentation/metadataTypes/${metadataTypeName}`,
        );
        return response.data;
      },
      [sweepApi],
    ),
    get_layout: useCallback(
      async (metadataId: string) => {
        const response = await sweepApi.get<LayoutProperties>(
          `/documentation/layouts/${metadataId}`,
        );
        return response.data;
      },
      [sweepApi],
    ),
    post_metadata: useCallback(
      async (metadataTypeName: string) => {
        await sweepApi.post(`/documentation/metadataTypes`, { metadataTypeName });
      },
      [sweepApi],
    ),
    delete_metadataType: useCallback(
      async (metadataTypeName: string) => {
        await sweepApi.delete(`/documentation/metadataTypes/${metadataTypeName}`);
      },
      [sweepApi],
    ),
    get_crmOrgConfigurationDependenciesFromSalto: useCallback(
      async ({
        crmOrgId,
        configurationName,
        configurationKey,
        objectName,
        filename,
      }: {
        crmOrgId: string;
        configurationName: string;
        configurationKey: string;
        objectName: string;
        filename: string;
      }) => {
        const searchParams = buildURLQuery({
          configurationKey,
          objectName,
          activeOnly: false,
          filename,
        });
        const queryString = searchParams ? '?' + searchParams : '';
        const { data } = await sweepApi.get<ConfigurationDependencies>(
          `/documentation/org/${crmOrgId}/configuration/${encodeURIComponent(configurationName)}/dependencies${queryString}`,
        );

        return data;
      },
      [sweepApi],
    ),
    get_crmOrgConfigurationConsumersFromSalto: useCallback(
      async ({
        crmOrgId,
        configurationName,
        configurationKey,
        objectName,
        filename,
      }: {
        crmOrgId: string;
        configurationName: string;
        configurationKey: string;
        objectName: string;
        filename: string;
      }) => {
        const searchParams = buildURLQuery({
          configurationKey,
          objectName,
          activeOnly: false,
          filename,
        });
        const queryString = searchParams ? '?' + searchParams : '';
        const response = await sweepApi.get(
          `/documentation/org/${crmOrgId}/configuration/${encodeURIComponent(configurationName)}/consumers${queryString}`,
        );

        return response.data as ConfigurationConsumers;
      },
      [sweepApi],
    ),
    get_crmOrgFieldConsumerFromSalto: useCallback(
      async ({
        crmOrgId,
        objectName,
        fieldName,
        filename,
      }: {
        crmOrgId: string;
        objectName: string;
        fieldName: string;
        filename: string;
      }) => {
        const searchParams = buildURLQuery({ objectName, fieldName, activeOnly: false, filename });

        const response = await sweepApi.get(
          `/documentation/org/${crmOrgId}/field-consumers?${searchParams}`,
        );
        return response.data as ConfigurationConsumers;
      },
      [sweepApi],
    ),
    post_aiConfigurationChat: useCallback(
      async (payload: AiChatConfigurationPayload) => {
        const { crmOrgId, ..._payload } = payload;
        const response = await sweepApi.post(`/documentation/org/${crmOrgId}/ai-chat`, _payload);
        return response.data as AiChatDto;
      },
      [sweepApi],
    ),
    post_aiFieldChat: useCallback(
      async (payload: AiChatFieldPayload) => {
        const { crmOrgId, ..._payload } = payload;
        const response = await sweepApi.post(`/documentation/org/${crmOrgId}/ai-chat`, _payload);
        return response.data as AiChatDto;
      },
      [sweepApi],
    ),
    post_crmOrgGenerateConfigurationGpt: useCallback(
      async ({
        crmOrgId,
        payload,
      }: {
        crmOrgId: string;
        payload:
          | {
              objectName: string;
              configurationKey: string;
              configurationName: string;
            }
          | {
              filename: string;
              configurationName: string;
            };
      }) => {
        const response = await sweepApi.post(
          `/documentation/org/${crmOrgId}/generate-configuration-gpt`,
          payload,
        );
        return response.data as OpenAiResponse;
      },
      [sweepApi],
    ),
    get_searchNameAndLabel: useCallback(
      async ({ crmOrgId, searchTxt }: { crmOrgId: string; searchTxt: string }) => {
        const response = await sweepApi.get(
          `/documentation/org/${crmOrgId}/search?searchText=${encodeURIComponent(searchTxt)}`,
        );
        return transformSearchResponse(response.data) as SearchResponse;
      },
      [sweepApi],
    ),
    get_searchContent: useCallback(
      async ({ crmOrgId, searchTxt }: { crmOrgId: string; searchTxt: string }) => {
        const response = await sweepApi.get(
          `/documentation/org/${crmOrgId}/search-content?searchText=${encodeURIComponent(searchTxt)}`,
        );
        return transformSearchResponse(response.data) as ContentSearchResponse;
      },
      [sweepApi],
    ),
    get_NaclFile: useCallback(
      async ({ crmOrgId, filename }: { crmOrgId: string; filename: string }) => {
        const response = await sweepApi.get(
          `/documentation/org/${crmOrgId}/nacl-file?filename=${filename}`,
        );
        return response.data as string;
      },
      [sweepApi],
    ),
  };
};
